import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { localeToBcp47 } from '../utils/locale';
import './TasksCalendarView.css';

// Monday-first (ISO / Swedish calendar convention)
const WEEKDAY_SHORT_KEYS = [
  'weekdayMonShort',
  'weekdayTueShort',
  'weekdayWedShort',
  'weekdayThuShort',
  'weekdayFriShort',
  'weekdaySatShort',
  'weekdaySunShort'
];

export const toDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const getTaskScheduleKey = (task) => {
  const raw = task.scheduled_for || task.created_at;
  return raw ? toDateKey(raw) : null;
};

function TasksCalendarView({
  tasks,
  month,
  onMonthChange,
  selectedDay,
  onSelectDay,
  renderTaskCard,
  canCreate = false,
  onCreateForDay
}) {
  const { t, i18n } = useTranslation(['tasks']);

  const tasksByDay = useMemo(() => {
    const map = new Map();
    tasks.forEach((task) => {
      const key = getTaskScheduleKey(task);
      if (!key) {
        return;
      }
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(task);
    });
    return map;
  }, [tasks]);

  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const mondayOffset = (firstDay.getDay() + 6) % 7;
  const cells = Array.from({ length: mondayOffset + daysInMonth }, (_, index) => {
    const day = index - mondayOffset + 1;
    return day > 0 ? new Date(month.getFullYear(), month.getMonth(), day) : null;
  });

  const shiftMonth = (offset) => {
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + offset, 1));
  };

  const selectedTasks = selectedDay ? (tasksByDay.get(selectedDay) || []) : [];
  const selectedLabel = selectedDay
    ? new Date(`${selectedDay}T12:00:00`).toLocaleDateString(localeToBcp47(i18n.language), {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      })
    : '';

  const agendaTitle = selectedTasks.length === 1
    ? t('tasks:selectedDayAgendaOne', { label: selectedLabel })
    : t('tasks:selectedDayAgenda', { label: selectedLabel, count: selectedTasks.length });

  return (
    <div className="tasks-calendar-layout">
      <section className="tasks-calendar-month card">
        <div className="tasks-calendar-header">
          <button
            type="button"
            className="tasks-calendar-nav"
            onClick={() => shiftMonth(-1)}
            aria-label={t('tasks:previousMonth')}
          >
            <span className="material-symbols-outlined" aria-hidden="true">chevron_left</span>
          </button>
          <h3 className="tasks-calendar-month-title">
            {month.toLocaleString(localeToBcp47(i18n.language), { month: 'long', year: 'numeric' })}
          </h3>
          <button
            type="button"
            className="tasks-calendar-nav"
            onClick={() => shiftMonth(1)}
            aria-label={t('tasks:nextMonth')}
          >
            <span className="material-symbols-outlined" aria-hidden="true">chevron_right</span>
          </button>
        </div>
        <div className="tasks-calendar-weekdays">
          {WEEKDAY_SHORT_KEYS.map((key) => (
            <span key={key}>{t(`tasks:${key}`)}</span>
          ))}
        </div>
        <div className="tasks-calendar-grid">
          {cells.map((date, index) => {
            if (!date) {
              return <span key={`empty-${index}`} className="tasks-calendar-day tasks-calendar-day--empty" />;
            }
            const key = toDateKey(date);
            const hasTasks = tasksByDay.has(key);
            const classes = [
              'tasks-calendar-day',
              hasTasks ? 'tasks-calendar-day--has-tasks' : '',
              selectedDay === key ? 'tasks-calendar-day--selected' : ''
            ].filter(Boolean).join(' ');
            return (
              <button
                key={key}
                type="button"
                className={classes}
                onClick={() => onSelectDay(key)}
              >
                <span className="tasks-calendar-day-num">{date.getDate()}</span>
                {hasTasks && <span className="tasks-calendar-day-dot" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </section>

      <section className="tasks-calendar-agenda">
        <div className="tasks-calendar-agenda-header">
          <h3 className="tasks-calendar-agenda-title">{agendaTitle}</h3>
          {canCreate && selectedDay && onCreateForDay && (
            <button
              type="button"
              className="btn btn-primary btn-sm tasks-calendar-create-btn"
              onClick={() => onCreateForDay(selectedDay)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">add</span>
              {t('tasks:fabCreateTask')}
            </button>
          )}
        </div>
        {selectedTasks.length > 0 ? (
          <div className="tasks-card-grid">
            {selectedTasks.map((task) => renderTaskCard(task))}
          </div>
        ) : (
          <p className="tasks-calendar-empty">{t('tasks:noTasksOnDay')}</p>
        )}
      </section>
    </div>
  );
}

export default TasksCalendarView;
